import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

/**
 * Hook pour gérer les erreurs API de manière internationalisée
 * Gère les erreurs communes, les messages d'erreur serveur, et les notifications
 */
export const useErrorHandler = () => {
  const { t } = useTranslation('reviews');
  const [error, setError] = useState(null);
  const [isError, setIsError] = useState(false);

  /**
   * Gère une erreur et affiche un message internationalisé
   * @param {Error|Object} err - L'erreur à gérer
   * @param {string} context - Contexte de l'erreur (ex: 'reviews.errors.loadFailed')
   */
  const handleError = useCallback((err, context = null) => {
    console.error('Error:', err);
    
    let errorMessage = '';
    let errorKey = '';

    // Erreur réseau
    if (!navigator.onLine) {
      errorKey = 'reviews.errors.networkError';
      errorMessage = t('reviews.errors.networkError');
    }
    // Erreur avec réponse serveur
    else if (err.response) {
      const { status, data } = err.response;

      switch (status) {
        case 400:
          // Mauvaise requête - utiliser le message du serveur si disponible
          if (data?.error?.includes('already')) {
            errorKey = 'reviews.errors.alreadyReviewed';
            errorMessage = t('reviews.errors.alreadyReviewed');
          } else if (data?.message) {
            errorMessage = data.message;
          } else {
            errorMessage = t('reviews.errors.invalidRating');
          }
          break;

        case 401:
          // Non authentifié - AFFICHER L'ERREUR SEULEMENT, NE PAS DÉCONNECTER
          errorKey = 'reviews.errors.notAuthorized';
          errorMessage = t('reviews.errors.notAuthorized');
          // NE PAS rediriger automatiquement - laisser l'utilisateur décider
          break;

        case 403:
          // Non autorisé
          errorKey = 'reviews.errors.notAuthorized';
          errorMessage = t('reviews.errors.notAuthorized');
          break;

        case 404:
          // Ressource introuvable
          errorMessage = t('reviews.errors.loadFailed');
          break;

        case 422:
          // Erreur de validation
          if (data?.errors) {
            // Afficher la première erreur de validation
            const firstError = Object.values(data.errors)[0];
            errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
          } else {
            errorMessage = t('reviews.errors.requiredField');
          }
          break;

        case 500:
        case 502:
        case 503:
          // Erreur serveur
          errorKey = 'reviews.errors.serverError';
          errorMessage = t('reviews.errors.serverError');
          break;

        default:
          errorMessage = data?.message || t('reviews.errors.serverError');
      }
    }
    // Erreur de requête (timeout, etc.)
    else if (err.request) {
      errorKey = 'reviews.errors.networkError';
      errorMessage = t('reviews.errors.networkError');
    }
    // Autre erreur
    else {
      errorMessage = err.message || t('reviews.errors.serverError');
    }

    // Si un contexte est fourni, l'utiliser
    if (context) {
      errorMessage = t(context);
    }

    // Mettre à jour l'état
    setError({ message: errorMessage, key: errorKey, raw: err });
    setIsError(true);

    // Afficher toast notification
    toast.error(errorMessage, {
      position: 'top-right',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });

    return errorMessage;
  }, [t]);

  /**
   * Réinitialise l'erreur
   */
  const clearError = useCallback(() => {
    setError(null);
    setIsError(false);
  }, []);

  /**
   * Affiche un message de succès internationalisé
   */
  const showSuccess = useCallback((messageKey, params = {}) => {
    const key = typeof messageKey === 'string' && !messageKey.startsWith('reviews.')
      ? `reviews.${messageKey}`
      : messageKey;
    const message = t(key, params);
    toast.success(message, {
      position: 'top-right',
      autoClose: 3000,
    });
  }, [t]);

  /**
   * Affiche un message d'info internationalisé
   */
  const showInfo = useCallback((messageKey, params = {}) => {
    const message = t(messageKey, params);
    toast.info(message, {
      position: 'top-right',
      autoClose: 3000,
    });
  }, [t]);

  return {
    error,
    isError,
    handleError,
    clearError,
    showSuccess,
    showInfo
  };
};

/**
 * Hook pour valider les données de review avant soumission
 */
export const useReviewValidation = () => {
  const { t } = useTranslation();

  const validateReview = useCallback((reviewData) => {
    const errors = {};

    // Validation overall_rating (requis)
    if (!reviewData.overall_rating) {
      errors.overall_rating = t('reviews.errors.requiredField');
    } else if (reviewData.overall_rating < 1 || reviewData.overall_rating > 5) {
      errors.overall_rating = t('reviews.errors.invalidRating');
    }

    // Validation overall_comment
    if (reviewData.overall_comment) {
      if (reviewData.overall_comment.length < 10) {
        errors.overall_comment = t('reviews.errors.commentTooShort', { min: 10 });
      } else if (reviewData.overall_comment.length > 1500) {
        errors.overall_comment = t('reviews.errors.commentTooLong', { max: 1500 });
      }
    }

    // Validation equipment_comment
    if (reviewData.equipment_comment && reviewData.equipment_comment.length > 1000) {
      errors.equipment_comment = t('reviews.errors.commentTooLong', { max: 1000 });
    }

    // Validation process_comment
    if (reviewData.process_comment && reviewData.process_comment.length > 1000) {
      errors.process_comment = t('reviews.errors.commentTooLong', { max: 1000 });
    }

    // Validation experience_comment
    if (reviewData.experience_comment && reviewData.experience_comment.length > 1000) {
      errors.experience_comment = t('reviews.errors.commentTooLong', { max: 1000 });
    }

    // Validation ratings (si fournis, doivent être entre 1 et 5)
    ['equipment_rating', 'process_rating', 'experience_rating'].forEach(field => {
      if (reviewData[field] !== undefined && reviewData[field] !== null) {
        if (reviewData[field] < 1 || reviewData[field] > 5) {
          errors[field] = t('reviews.errors.invalidRating');
        }
      }
    });

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }, [t]);

  return { validateReview };
};

export default useErrorHandler;
